import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { AppState } from '@square-photo/types/app-state.types';
import type {
  PhotoFile,
  ProcessedPhoto,
  ProcessingError,
} from '@square-photo/types/photo.types';

export const MAX_PHOTOS = 20;

const initialState: AppState = {
  phase: 'idle',
  photos: [],
  processedPhotos: [],
  currentProcessingIndex: 0,
  zipFilename: '',
};

/*
 * Actions 只負責狀態轉換，不含 side effect（URL 建立/釋放、Worker 通訊）。
 * Side effect 統一由 use-photo-converter.ts 管理。
 */
interface PhotoConverterStoreActions {
  addPhotos: (photoFiles: PhotoFile[]) => void;
  /* URL revoke 由 hook 在呼叫前處理 */
  removePhoto: (id: string) => void;
  startProcessing: (zipFilename: string) => void;
  setPhotoProcessing: (id: string, index: number) => void;
  setPhotoDone: (result: ProcessedPhoto) => void;
  setPhotoError: (id: string, error: ProcessingError) => void;
  setAllDone: () => void;
  /* URL revoke 由 hook 在呼叫前處理 */
  reset: () => void;
}

export const usePhotoConverterStore = create<
  AppState & PhotoConverterStoreActions
>()(
  immer((set) => ({
    ...initialState,

    addPhotos: (photoFiles) =>
      set((state) => {
        state.photos = [...state.photos, ...photoFiles].slice(0, MAX_PHOTOS);
        state.phase = 'selecting';
      }),

    removePhoto: (id) =>
      set((state) => {
        state.photos = state.photos.filter((p) => p.id !== id);
        state.phase = state.photos.length === 0 ? 'idle' : 'selecting';
      }),

    startProcessing: (zipFilename) =>
      set((state) => {
        state.phase = 'processing';
        state.processedPhotos = [];
        state.currentProcessingIndex = 0;
        state.zipFilename = zipFilename;
      }),

    setPhotoProcessing: (id: string, index: number) =>
      set((state: AppState) => {
        state.currentProcessingIndex = index;
        const photo = state.photos.find((p) => p.id === id);
        if (photo) photo.status = 'processing';
      }),

    setPhotoDone: (result: ProcessedPhoto) =>
      set((state: AppState) => {
        const photo = state.photos.find((p) => p.id === result.id);
        if (photo) photo.status = 'done';
        state.processedPhotos.push(result);
      }),

    setPhotoError: (id: string, error: ProcessingError) =>
      set((state: AppState) => {
        const photo = state.photos.find((p) => p.id === id);
        if (photo) photo.status = 'error';
        state.processedPhotos.push({
          id,
          filename: '',
          blob: new Blob(),
          status: 'error',
          error,
        });
      }),

    setAllDone: () =>
      set((state: AppState) => {
        state.phase = 'done';
      }),

    reset: () =>
      set((state: AppState) => {
        state.phase = 'idle';
        state.photos = [];
        state.processedPhotos = [];
        state.currentProcessingIndex = 0;
        state.zipFilename = '';
      }),
  })),
);
